/**
 * Practice Leaderboard (CRM web) — team-wide 30-day ranking driven by
 * pillar_score_rollups.  Rank / podium / streak / trend per rep.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, Loader2, Flame } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Leaderboard() {
    const { getAuthHeader } = useAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(30);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API}/role-play/leaderboard`, {
                    headers: getAuthHeader(),
                    params: { days },
                });
                setRows(res.data?.leaderboard || []);
            } catch (e) {
                toast.error('Failed to load leaderboard');
            } finally {
                setLoading(false);
            }
        })();
    }, [getAuthHeader, days]);

    const topThree = rows.slice(0, 3);
    const podium = topThree.length >= 1 ? [topThree[1], topThree[0], topThree[2]].filter(Boolean) : [];

    return (
        <div className="max-w-5xl mx-auto p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Practice Leaderboard
                    </h1>
                    <p className="text-sm text-stone-500 mt-1">
                        Team ranking · last {days} days · driven by Role Play + Micro-Practice scores
                    </p>
                </div>
                <select
                    value={days}
                    onChange={e => setDays(Number(e.target.value))}
                    className="px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm"
                >
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                </select>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                </div>
            ) : rows.length === 0 ? (
                <div className="bg-white border border-stone-200 rounded-xl p-12 text-center">
                    <Trophy className="w-12 h-12 text-stone-200 mx-auto mb-3" />
                    <h2 className="text-lg font-semibold text-stone-800 mb-1">No practice sessions yet</h2>
                    <p className="text-sm text-stone-500">Run your first Role Play to appear on the board.</p>
                </div>
            ) : (
                <>
                    {/* Podium */}
                    {podium.length > 0 && (
                        <div className="flex items-end justify-center gap-5 mb-10">
                            {podium.map((r) => {
                                const pos = r.rank;
                                const h = pos === 1 ? 160 : pos === 2 ? 120 : 100;
                                const medal = pos === 1 ? '🥇' : pos === 2 ? '🥈' : '🥉';
                                return (
                                    <div key={r.user_id} className="text-center w-40">
                                        <div className="text-3xl mb-2">{medal}</div>
                                        <div
                                            className="mx-auto mb-2 flex items-center justify-center rounded-full text-xl font-bold"
                                            style={{
                                                width: 64, height: 64,
                                                background: 'rgba(93,63,211,0.15)',
                                                color: '#5D3FD3',
                                                border: pos === 1 ? '3px solid #F2A423' : 'none',
                                            }}
                                        >{r.initials}</div>
                                        <div className="text-sm font-bold">{r.name}</div>
                                        <div className="text-xl font-bold text-violet-600 tracking-tight">{r.avg_score}</div>
                                        <div
                                            className="mt-2 rounded-t-xl opacity-30"
                                            style={{
                                                height: h,
                                                background: `linear-gradient(180deg, ${pos === 1 ? '#F2A423' : '#8E7AF5'}, transparent)`,
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Full list */}
                    <div className="space-y-2">
                        {rows.map((r) => (
                            <div
                                key={r.user_id}
                                className={`flex items-center gap-4 px-4 py-3 rounded-xl border ${r.is_you ? 'bg-violet-50 border-violet-200' : 'bg-white border-stone-200'}`}
                            >
                                <div className="w-8 text-center text-sm font-bold text-stone-400">{r.rank}</div>
                                <div
                                    className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                                    style={{ background: 'rgba(93,63,211,0.15)', color: '#5D3FD3' }}
                                >{r.initials}</div>
                                <div className="flex-1">
                                    <div className="text-sm font-bold">
                                        {r.name}
                                        {r.is_you && <span className="ml-2 text-xs text-violet-600">· You</span>}
                                    </div>
                                    <div className="text-xs text-stone-500 flex items-center gap-2">
                                        {r.sessions_count} sessions
                                        {r.streak_days > 0 && (
                                            <span className="flex items-center gap-0.5">
                                                <Flame className="w-3 h-3 text-amber-500" />
                                                {r.streak_days} day streak
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="text-xl font-bold tracking-tight w-16 text-right">{r.avg_score}</div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
